import { ButtonBase } from '@mui/material';
import { memo } from 'react';

import useStyles from './SuggestionChip.styles';

export type SuggestionChipProps = {
  /**
   * Stable identifier. Doubles as the `selectedChipId` value in
   * Redux and as the chip's test-id suffix
   * (`ai-sidekick-chip-<id>`), so it must be stable across renders.
   */
  id: string;
  /**
   * Visible chip label. Caller is responsible for resolving any
   * translation key — the chip is namespace-agnostic.
   */
  label: string;
  /** Whether the chip is currently the active selection. */
  selected?: boolean;
  /** Click handler. */
  onClick?: () => void;
  /** Optional className appended to the root element. */
  className?: string;
};

/**
 * Pure presentational suggestion-chip primitive. Rendered by both
 * the entry-card (`<SidekickSuggestionsCard />`) and the panel
 * zero-state (`<PanelZeroStateSuggestions />`).
 *
 * Pure means: no Redux access, no translation lookup, no flag
 * gating. The caller resolves the visible label and owns the click
 * → dispatch flow. The chip wires:
 *
 * - `aria-pressed` for the selected state.
 * - `data-testid="ai-sidekick-chip-<id>"` for unit + e2e tests.
 * - The shared chip CSS (pill radius, border, hover/focus/selected
 *   variants).
 */
function SuggestionChipInner({
  id,
  label,
  selected,
  onClick,
  className,
}: SuggestionChipProps) {
  const { classes, cx } = useStyles();
  const isSelected = !!selected;

  return (
    <ButtonBase
      className={cx(
        classes.chip,
        { [classes.chipSelected]: isSelected },
        className
      )}
      onClick={onClick}
      aria-pressed={isSelected}
      data-testid={`ai-sidekick-chip-${id}`}
    >
      {label}
    </ButtonBase>
  );
}

const SuggestionChip = memo(SuggestionChipInner);
SuggestionChip.displayName = 'SuggestionChip';

export default SuggestionChip;
